import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Image, ImageFit } from '@fluentui/react/lib/Image';

import {
  getImageUrlById,
  transformDateForEditMode,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  useSiteConfig,
  useIsSmartPageCustomizedUpdateEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { TooltipWrapper } from '@bingads-webui-campaign-react/smart-component-primitive';
import { LogoImage, proxyReadLogoConfig, } from '@bingads-webui-campaign-react/smart-component-image';

import { UPDATE_CARD_MODE, UPDATE_SOURCE } from './constants';

const getStyles = (_, { palette = {} }) => ({
  userInfoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: 'calc(100% - 20px)',
    marginBottom: 9,
  },
  logoImageContainerWithIcon: {
    width: 'calc(100% - 20px)',
  },
  logoImageContainerWithoutIcon: {
    width: '100%',
  },
  smallContent: {
    height: 175,
  },
  largeContent: {
    height: 268,
  },
  [UPDATE_CARD_MODE.EDIT_MODE]: {
    '& .card': {
      position: 'relative',
    },
    '& .photo': {
      display: 'block',
      width: 332,
      height: 211,
    },
    '& .message': {
      display: 'block',
      wordBreak: 'break-word',
      whiteSpace: 'pre-line',
    },
  },
  [UPDATE_CARD_MODE.VIEW_MODE]: {
    marginBottom: 60,
    '& .card': {
      position: 'relative',
      padding: '20px 0 42px 0',
      margin: '0px 15px',
      width: 340,
      height: 360,
      boxShadow: '0px 1.6px 3.6px rgba(0, 0, 0, 0.132), 0px 0.3px 0.9px rgba(0, 0, 0, 0.108)',
      borderRadius: 2,
      background: palette.cardBackground,
    },
    '& .photo': {
      display: 'block',
      width: 300,
      padding: '0 15px',
      objectFit: 'contain',
      maxHeight: '268px',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    '& .message': {
      display: 'block',
      wordBreak: 'break-word',
      whiteSpace: 'pre-line',
      width: 300,
      marginTop: 8,
      marginLeft: 'auto',
      marginRight: 'auto',
      color: palette.secondaryText,
    },
  },
  createdTimeInViewMode: {
    position: 'absolute',
    right: 12,
    bottom: 12,
    color: palette.tertiaryText,
    fontSize: 12,
  },
  fbIcon: {
    color: '#1877F2',
    fontSize: 18,
    lineHeight: '18px',
    fontFamily: 'MsAds MDL2 Assets',
    '&:before': {
      content: '"\\ECB3"',
    },
  },
});

const UnStyledSmartUpdateCard = ({
  item,
  classes,
  i18n,
  mode,
}) => {
  const { siteConfig } = useSiteConfig();
  const isSmartPageCustomizedUpdateEnabled = useIsSmartPageCustomizedUpdateEnabled();
  const src = getImageUrlById(siteConfig, item.imageId) || item.photo;
  const { label, imageId } = proxyReadLogoConfig({ i18n, siteConfig });

  const updateSourceIcon = useMemo(() => {
    if (!isSmartPageCustomizedUpdateEnabled) {
      return null;
    }

    if (item && (item.source === UPDATE_SOURCE.FACEBOOK || !item.source)) {
      return (<div className={classNames('update-source-icon-fb', classes.fbIcon)} />);
    }

    return null;
  }, [item, classes, isSmartPageCustomizedUpdateEnabled]);

  const getUpdateContent = useCallback(() => (mode === UPDATE_CARD_MODE.EDIT_MODE ?
    (
      <div>
        {
          src &&
          <div>
            <Image
              src={src}
              alt=""
              className="photo"
              imageFit={ImageFit.contain}
            />
          </div>
        }
        <div className="message">
          {item.message}
        </div>
      </div>
    )
    :
    (
      <div>
        {
          src &&
          <div>
            <img
              src={src}
              alt=""
              className={classNames('photo', item.message ? classes.smallContent : null)}
              imageFit={ImageFit.contain}
            />
          </div>
        }
        <TooltipWrapper content={item.message} lineClamp={6}>
          <div className="message">
            {item.message}
          </div>
        </TooltipWrapper>
      </div>
    )
  ), [mode, src, item.message, classes]);

  return (
    <div className={classes[mode]} >
      <div className="card" >
        {
          mode === UPDATE_CARD_MODE.EDIT_MODE &&
          (
            <div className={classes.userInfoContainer}>
              <div className={updateSourceIcon ?
                classes.logoImageContainerWithIcon : classes.logoImageContainerWithoutIcon}
              >
                <LogoImage
                  imageId={imageId}
                  label={label}
                  timestamp={transformDateForEditMode(item.createdTime, i18n)}
                />
              </div>
              {updateSourceIcon}
            </div>
          )
        }
        {getUpdateContent()}
        {
          mode === UPDATE_CARD_MODE.VIEW_MODE &&
          (
            <div className={classNames(classes.createdTimeInViewMode, 'created-time')}>
              {transformDateForEditMode(item.createdTime, i18n)}
            </div>
          )
        }
      </div>
    </div>
  );
};

UnStyledSmartUpdateCard.propTypes = {
  item: PropTypes.shape({
    message: PropTypes.string,
    photo: PropTypes.string,
    photoId: PropTypes.string,
  }).isRequired,
  classes: PropTypes.shape({
    userInfoContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  mode: PropTypes.string,
};

UnStyledSmartUpdateCard.defaultProps = {
  mode: UPDATE_CARD_MODE.EDIT_MODE,
};

export const SmartUpdateCard = withDefaultStyles(
  UnStyledSmartUpdateCard,
  getStyles
);
